/**
 * MiniStat — small label-above-value column.
 *
 * Moved from `widgets/ihs/IhsPrimitives.tsx` (Phase 0b). Label is 11px
 * grey, value is 18px bold. Used in the IhsOverview 5-col row
 * (PL / V / D / L / Pts) and any future "label / number" stat block.
 *
 * Shape: { label: string; value: ReactNode; valueColor?: string }
 */

import type { ReactElement, ReactNode } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './MiniStat.module.css';

/**
 * `value` is `ReactNode` at the component level so consumers can pass
 * a number, a string, or a styled span. The Zod schema captures the
 * portable wire-shape — string or number — which is what an LLM would
 * supply from data. A widget that wraps MiniStat in JSX is free to
 * substitute richer nodes; the planner just needs to know "this slot
 * holds a value".
 */
const dataShape = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  /** Optional override for the value colour (e.g. green for Pts). */
  valueColor: z.string().optional(),
});

export const META: PrimitiveMeta = {
  name: 'mini-stat',
  description: 'Tiny label above a bold value (e.g. PL/V/D/L/Pts column).',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'count-up',
};

interface MiniStatProps {
  label: string;
  value: ReactNode;
  /** Optional override for the value colour (e.g. green for Pts). */
  valueColor?: string;
}

export function MiniStat({
  label,
  value,
  valueColor,
}: MiniStatProps): ReactElement {
  return (
    <div className={styles.miniStat}>
      <span className={styles.label}>{label}</span>
      <span
        className={styles.value}
        {...(valueColor !== undefined ? { style: { color: valueColor } } : {})}
      >
        {value}
      </span>
    </div>
  );
}

export default MiniStat;
